import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import ShadowBox from '../chrome/ShadowBox';
import Divider from '../chrome/Divider';
import Spacing from '../layout/Spacing';
import PickItem from './PickItem';
import Drawer from '../chrome/Drawer';

const containerBaseStyles = () => css`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const Container = styled('div')(containerBaseStyles);

const drawerHeaderBaseStyles = ({ isSubmitted, theme }) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: ${isSubmitted ? theme.colors.g200 : theme.colors.white};
  border-radius: 8px;
`;

const DrawerHeader = styled('div')(drawerHeaderBaseStyles);

const DrawerItem = styled('div')();

const ShadowBoxWrapper = styled(ShadowBox)`
  border-radius: ${({ theme }) => theme.borderRadius.giga};
  box-shadow: none;
  border: ${({ isSubmitted, theme }) => (isSubmitted ? 'none' : `1px solid ${theme.colors.n100}`)};
`;

const CardWithDrawer = ({
  id,
  isPlaceholder,
  assetUrl,
  secondaryAssetUrl,
  headerAssetHasPadding,
  subText,
  text,
  items,
  drawerDefault,
  drawerOpen,
  closeDelay,
  isSubmitted,
  selectedItem,
  handleDrawerToggle,
  handleItemClick,
}) => {
  const drawerHeaderRef = useRef(null);

  const selectedClosed = selectedItem && !drawerOpen;
  const selectedSubmitted = !drawerOpen && isSubmitted;
  const closedSelectedSubmitted = selectedClosed || selectedSubmitted;
  const headerVariant = closedSelectedSubmitted ? 'leftSelected' : 'centered';
  const closedNotSelected = drawerOpen ? 'chevron-up' : 'chevron-down';
  const headerIcon = isSubmitted ? 'lock' : closedNotSelected;
  const onItemClick = (id) => () => {
    if (drawerHeaderRef && drawerHeaderRef.current) {
      drawerHeaderRef.current.scrollIntoView({
        block: 'center',
      });
    }
    handleItemClick(id);
  };
  return (
    <ShadowBoxWrapper isSubmitted={isSubmitted}>
      <Container>
        <Spacing>
          <DrawerHeader
            ref={drawerHeaderRef}
            isSubmitted={isSubmitted}
            {...(!isSubmitted && { onClick: () => handleDrawerToggle(id) })}
          >
            <PickItem
              isPlaceholder={isPlaceholder}
              variant={headerVariant}
              assetUrl={assetUrl}
              secondaryAssetUrl={secondaryAssetUrl}
              subText={subText}
              text={text}
              icon={headerIcon}
              isClickable={!isSubmitted}
              assetHasPadding={headerAssetHasPadding}
            />
          </DrawerHeader>
        </Spacing>
        {!isSubmitted && (
          <Drawer default={drawerDefault} toggle={drawerOpen} delay={closeDelay}>
            {items.map((item, index) => (
              <DrawerItem key={index} onClick={onItemClick(item.id)}>
                <Spacing sizeLeft='kilo' sizeRight='kilo'>
                  <Divider />
                  <PickItem
                    variant='left'
                    icon={selectedItem === item.id ? 'radioActive' : 'radio'}
                    isClickable
                    reducePadding
                    {...item}
                  />
                </Spacing>
              </DrawerItem>
            ))}
          </Drawer>
        )}
      </Container>
    </ShadowBoxWrapper>
  );
};

CardWithDrawer.propTypes = {
  id: PropTypes.number,
  isPlaceholder: PropTypes.bool,
  assetUrl: PropTypes.string,
  secondaryAssetUrl: PropTypes.string,
  headerAssetHasPadding: PropTypes.bool,
  subText: PropTypes.string,
  isSubmitted: PropTypes.bool,
  text: PropTypes.string,
  items: PropTypes.array,
  selectedItem: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  drawerDefault: PropTypes.string,
  drawerOpen: PropTypes.bool,
  closeDelay: PropTypes.func,
  handleDrawerToggle: PropTypes.func,
  handleItemClick: PropTypes.func,
};

export default CardWithDrawer;
